/**
 * Handlingar och skäl i "Hantera flagga" (#30).
 *
 * NYCKELN lagras i databasen, ETIKETTEN visas. Den uppdelningen är avsiktlig: skälen är
 * mätinstrumentet för hur väl flaggorna fungerar, och lagrades visningstexten skulle
 * statistiken spricka i två varje gång någon formulerar om en rad.
 *
 * De två handlingarna mäter olika saker och hålls därför isär:
 *   avfärdad — påstående om FLAGGAN. Återkoppling till flaggbygget.
 *   hanterad — påstående om ARBETET. Säger ingenting om flaggan.
 *
 * Slås de ihop går falsk-positiv-frekvensen inte längre att läsa av på handlingen.
 *
 * Skälen kommer från Sollentuna-pilotens faktiska triage-kategorier. Listorna är
 * medvetet korta — de ska kunna väljas på en sekund, inte läsas.
 */

export type CaseKind = 'avfärdad' | 'hanterad'

export interface Reason {
  key: string
  label: string
}

export interface ActionConfig {
  kind: CaseKind
  title: string
  description: string
  reasons: Reason[]
}

/** Visningsnamn per handling. `uppföljning` finns bara på rader från gamla flödet. */
export const KIND_LABEL: Record<string, string> = {
  avfärdad: 'Avfärdad',
  hanterad: 'Hanterad',
  uppföljning: 'Uppföljning',
}

export const ACTIONS: ActionConfig[] = [
  {
    kind: 'hanterad',
    title: 'Markera som hanterad',
    description: 'Flaggan är hanterad',
    reasons: [
      { key: 'nyligen_uppföljd', label: 'Ärendet har nyligen följts upp' },
      { key: 'hembesök_inbokat', label: 'Brukaren har ett hembesök inbokat' },
      { key: 'diskuterad_med_utförare', label: 'Flaggan har diskuterats med utförare' },
    ],
  },
  {
    kind: 'avfärdad',
    title: 'Avfärda som felaktig',
    description: 'Flaggan är felaktig och borde inte skapats',
    reasons: [
      { key: 'fel_målgrupp', label: 'Flaggan träffar fel målgrupp' },
      { key: 'felaktig_information', label: 'Flaggan bygger på felaktig information' },
      { key: 'normalt_beteende', label: 'Flaggan reagerade på ett normalt beteende' },
    ],
  },
]

/**
 * Slår upp etiketten för en skäl-nyckel. Faller tillbaka på nyckeln om den inte känns
 * igen — en historisk rad ska visa NÅGOT även om ett skäl senare plockas bort ur listan.
 * Det är hela poängen med att lagra nycklar i stället för visningstext.
 */
export const reasonLabel = (key: string): string => ACTIONS.flatMap((a) => a.reasons).find((r) => r.key === key)?.label ?? key
